import dayjs from 'dayjs'
import type { DashboardUserProfile } from './normalizers'
import { getCurrentFirebaseUser, getFirebaseFirestore } from './client'
import { getHanaspeakApiBaseUrl } from './functions-api'
import { traceLearningPerformance } from '@shared/learning/learning-performance-trace'
import {
  buildSelfLearningStepMapKey,
  buildWorkbookProgressMapKey,
} from './progress-map-keys'
import type {
  LearningContentItem,
  LearningLesson,
  LearningWorkbookChapter,
  LearningWorkbookQuizItem,
} from './learning'
export { buildSelfLearningStepMapKey, buildWorkbookProgressMapKey }

export interface LearningSelfStudyProgress {
  readonly current_chapter?: string
  readonly id: string
  readonly learning_program?: string
  readonly userId?: string
}

export interface LearningDailyActivity {
  readonly createdAt: number
  readonly id?: string
  readonly seconds?: number
  readonly userId?: string
}

export interface LearningSelfLearningEntry {
  readonly chapterId: string
  readonly createdAt: number
  readonly selftStudyId: string
}

const MAX_DAILY_ACTIVITY_ENTRY_SECONDS = 12 * 60 * 60

function requireSelfStudyFirestore() {
  const firestore = getFirebaseFirestore()

  if (!firestore) {
    throw new Error('Cloud Firestore is not configured for this environment.')
  }

  return firestore
}

function requireCurrentUser() {
  const user = getCurrentFirebaseUser()

  if (!user) {
    throw new Error('Not authorized')
  }

  return user
}

function sortByNumericOrder<T extends { readonly sort?: number }>(items: T[]) {
  return [...items].sort((left, right) => {
    const leftSort = typeof left.sort === 'number' ? left.sort : 0
    const rightSort = typeof right.sort === 'number' ? right.sort : 0

    return leftSort - rightSort
  })
}

function chunkArray<T>(items: readonly T[], chunkSize: number) {
  const chunks: T[][] = []

  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize))
  }

  return chunks
}

function normalizeSortValue(sort: unknown) {
  if (typeof sort === 'number' && Number.isFinite(sort)) {
    return sort
  }

  if (typeof sort === 'string') {
    const parsedSort = Number(sort)

    if (Number.isFinite(parsedSort)) {
      return parsedSort
    }
  }

  return undefined
}

export function normalizeDailyActivityEntrySeconds(seconds: number) {
  if (!Number.isFinite(seconds)) {
    return 0
  }

  return Math.min(
    MAX_DAILY_ACTIVITY_ENTRY_SECONDS,
    Math.max(0, Math.round(seconds)),
  )
}

export function getElapsedDailyActivitySeconds({
  endedAt = Date.now(),
  startedAt,
}: {
  readonly endedAt?: number
  readonly startedAt: number | null | undefined
}) {
  if (
    typeof startedAt !== 'number' ||
    !Number.isFinite(startedAt) ||
    !Number.isFinite(endedAt) ||
    endedAt <= startedAt
  ) {
    return 0
  }

  return normalizeDailyActivityEntrySeconds((endedAt - startedAt) / 1000)
}

export async function getFullLessons(bookId: string): Promise<LearningLesson[]> {
  const firestore = requireSelfStudyFirestore()
  const [lessonSnapshot, chapterSnapshot, quizSnapshot] = await Promise.all([
    traceLearningPerformance({
      event: 'selfStudy.lessons.query',
      getDetails: (snapshot) => ({ bookId, docs: snapshot.size }),
      task: () =>
        firestore
          .collection('lessons')
          .where('status', '==', 'published')
          .where('bookId', '==', bookId)
          .get(),
    }),
    traceLearningPerformance({
      event: 'selfStudy.chapters.query',
      getDetails: (snapshot) => ({ bookId, docs: snapshot.size }),
      task: () =>
        firestore
          .collection('vietnamese_workbooks_chapters')
          .where('status', '==', 'published')
          .where('bookId', '==', bookId)
          .get(),
    }),
    traceLearningPerformance({
      event: 'selfStudy.listeningWorkbooks.query',
      getDetails: (snapshot) => ({ bookId, docs: snapshot.size }),
      task: () =>
        firestore
          .collection('workbooks')
          .where('status', '==', 'published')
          .where('bookId', '==', bookId)
          .where('template', '!=', 'horizontal_rule')
          .get(),
    }),
  ])

  const lessons = sortByNumericOrder(
    lessonSnapshot.docs.map((document) => ({
      ...(document.data() as Omit<LearningLesson, 'id'>),
      id: document.id,
      sort: normalizeSortValue(document.data().sort),
    })),
  )

  const chapters = sortByNumericOrder(
    chapterSnapshot.docs.map((document) => ({
      ...(document.data() as Omit<LearningWorkbookChapter, 'id'>),
      id: document.id,
      sort: normalizeSortValue(document.data().sort),
    })),
  )

  const listeningWorkbooks = sortByNumericOrder(
    quizSnapshot.docs.map((document) => ({
      ...(document.data() as Omit<LearningWorkbookQuizItem, 'id'>),
      id: document.id,
      sort: normalizeSortValue(document.data().sort),
    })),
  )

  return lessons.map((lesson) => ({
    ...lesson,
    chapters: chapters.filter((chapter) => chapter.lessonId === lesson.id),
    listening_workbooks: listeningWorkbooks.filter(
      (workbook) => workbook.lessonId === lesson.id,
    ),
  }))
}

export async function getSelfStudyContent(
  chapterId: string,
): Promise<LearningContentItem[]> {
  const firestore = requireSelfStudyFirestore()
  const snapshot = await traceLearningPerformance({
    event: 'selfStudy.content.query',
    getDetails: (querySnapshot) => ({ chapterId, docs: querySnapshot.size }),
    task: () =>
      firestore
        .collection('vietnamese_workbooks')
        .where('status', '==', 'published')
        .where('chapter', '==', chapterId)
        .orderBy('sort', 'asc')
        .get(),
  })

  return sortByNumericOrder(
    snapshot.docs.map((document) => ({
      ...(document.data() as Omit<LearningContentItem, 'id'>),
      id: document.id,
      sort: normalizeSortValue(document.data().sort),
    })),
  )
}

export async function getSelfStudyContentByChapterIds(
  chapterIds: readonly string[],
): Promise<LearningContentItem[]> {
  const uniqueChapterIds = [...new Set(chapterIds.filter(Boolean))]

  if (uniqueChapterIds.length === 0) {
    return []
  }

  const firestore = requireSelfStudyFirestore()
  const chapterIdChunks = chunkArray(uniqueChapterIds, 10)
  const snapshots = await Promise.all(
    chapterIdChunks.map((chapterIdChunk, chunkIndex) =>
      traceLearningPerformance({
        event: 'selfStudy.contentByChapterIds.query',
        getDetails: (snapshot) => ({
          chunk: chunkIndex + 1,
          chunkSize: chapterIdChunk.length,
          docs: snapshot.size,
          totalChunks: chapterIdChunks.length,
        }),
        task: () =>
          firestore
            .collection('vietnamese_workbooks')
            .where('status', '==', 'published')
            .where('chapter', 'in', chapterIdChunk)
            .get(),
      }),
    ),
  )
  const contentItems = snapshots.flatMap((snapshot) =>
    snapshot.docs.map((document) => ({
      ...(document.data() as Omit<LearningContentItem, 'id'>),
      id: document.id,
      sort: normalizeSortValue(document.data().sort),
    })),
  )

  return sortByNumericOrder(contentItems)
}

export async function saveSelfStudyStepProgress({
  bookId,
  chapterId,
  selfStudyId,
}: {
  readonly bookId: string
  readonly chapterId: string
  readonly selfStudyId: string
}): Promise<Record<string, unknown>> {
  const user = requireCurrentUser()
  const idToken = await user.getIdToken()
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/mobile/progress/self-study`, {
    body: JSON.stringify({ bookId, chapterId, selfStudyId }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
  const payload = await response.json().catch(() => null) as {
    readonly error?: unknown
    readonly personalization?: Record<string, unknown>
  } | null
  if (!response.ok || !payload?.personalization) {
    throw new Error(
      typeof payload?.error === 'string'
        ? payload.error
        : 'Failed to save self-study progress.',
    )
  }
  return payload.personalization
}

export async function upsertSelfStudyChapterLearning({
  currentChapterId,
  existingId,
  learningProgram,
}: {
  readonly currentChapterId: string
  readonly existingId?: string
  readonly learningProgram: string
}): Promise<LearningSelfStudyProgress> {
  const user = requireCurrentUser()
  const idToken = await user.getIdToken()
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/program/chapter/open`, {
    body: JSON.stringify({ bookId: learningProgram, chapterId: currentChapterId, existingId }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
  const payload = await response.json().catch(() => null) as {
    readonly error?: unknown
    readonly progress?: LearningSelfStudyProgress
  } | null
  if (!response.ok || !payload?.progress) {
    throw new Error(
      typeof payload?.error === 'string'
        ? payload.error
        : 'Unable to open the chapter right now.',
    )
  }
  return payload.progress
}

export async function addDailyActivityEntry({
  seconds,
}: {
  readonly seconds: number
}): Promise<LearningDailyActivity> {
  const user = requireCurrentUser()
  const createdAt = dayjs(new Date(dayjs().format('YYYY-MM-DD'))).valueOf()
  const idToken = await user.getIdToken()
  return traceLearningPerformance({
    event: 'selfStudy.dailyActivity.save',
    task: async () => {
      const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/activity/daily`, {
        body: JSON.stringify({ createdAt, seconds: normalizeDailyActivityEntrySeconds(seconds) }),
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
      const payload = await response.json().catch(() => null) as {
        readonly activity?: LearningDailyActivity
        readonly error?: unknown
      } | null
      if (!response.ok || !payload?.activity) {
        throw new Error(
          typeof payload?.error === 'string'
            ? payload.error
            : 'Unable to save study activity right now.',
        )
      }
      return payload.activity
    },
  })
}

export async function saveWorkbookQuizProgress({
  bookId,
  lessonId,
  rating,
}: {
  readonly bookId?: string
  readonly lessonId: string
  readonly rating: string
}) {
  const user = requireCurrentUser()
  const idToken = await user.getIdToken()
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/mobile/progress/listening`, {
    body: JSON.stringify({ bookId, lessonId, rating }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
  const payload = await response.json().catch(() => null) as {
    readonly progressWorkbookLessons?: DashboardUserProfile['progress_workbook_lessons']
    readonly error?: unknown
  } | null
  if (!response.ok || !Array.isArray(payload?.progressWorkbookLessons)) {
    throw new Error(
      typeof payload?.error === 'string'
        ? payload.error
        : 'Unable to save listening progress right now.',
    )
  }
  return payload.progressWorkbookLessons
}
